import { Button, Dropdown, Icon, notification, Table, Tabs } from 'antd';
import moment from 'moment';
import * as React from 'react';
import { getTerminationReport, IAttritionByDefault, IAttritionByRequest, ITerminationRequest } from '../apis/Terminations';
import '../assets/css/terminations.css';
import TerminationMenuItem from './terminations/TerminationMenuItem';
import { EOL } from 'os';

const TabPane = Tabs.TabPane;

interface ILocalProps {
  logoutFunc: Function,
}

interface ILocalState {
    loading: boolean,
    currentMonth: moment.Moment,
    attritionsByDefault: IAttritionByDefault[] | null,
    attritionsByRequest: IAttritionByRequest[] | null,
    requestedInMonth: ITerminationRequest[] | null,
    terminateInMonth: ITerminationRequest[] | null,
}

interface ITableCell {
    align?: "center" | "left" | "right" | undefined,
    dataIndex?: string,
    key: string,
    render?: (text: string, record: any) => JSX.Element,
    title: string,
}


class Terminations extends React.Component<ILocalProps, ILocalState> {
    public requestColumns: Array<ITableCell> = [
        {
            dataIndex: 'requestDate',
            key: 'requestDate',
            title: 'Request Date',
        },
        {
            dataIndex: 'terminationDate',
            key: 'terminationDate',
            title: 'Termination Date',
        },
        {
            dataIndex: 'lastInvoiceDate',
            key: 'lastInvoiceDate',
            title: 'Last Invoice Date',
        },
        {
            dataIndex: 'companyInfo.id',
            key: 'companyId',
            title: 'Company ID',
        },    
        {
            dataIndex: 'companyInfo.companyName',
            key: 'companyName',
            title: 'Company Name',
        },
        {
            align: 'center',
            dataIndex: 'isProcessed',
            key: 'isProcessed',
            render: (text: string, record: ITerminationRequest) => (
                <span>
                    {record.isProcessed ? <Icon type="check" /> : <Icon type="close" />}
                </span>
            ),
            title: 'Is Processed',
        },
        {
            dataIndex: 'note.content',
            key: 'note',
            title: 'Note',
        },
        {
            dataIndex: 'userProfileID',
            key: 'userProfileID',
            title: 'Author',
        },
        {
            dataIndex: 'createTimestamp',
            key: 'createTimestamp',
            title: 'Submission Date',
        },
        {
            key: 'actions',
            render: (text: string, record: ITerminationRequest) => (
                <span>

                    {!record.isProcessed &&
                        <Dropdown overlay={
                            <TerminationMenuItem
                                companyID={record.companyProfileID ? record.companyProfileID : 0}
                                companyName={record.companyInfo ? record.companyInfo.companyName : `${record.companyProfileID}`}
                                onChange={this.getReportData}
                                terminationRequestID={record.id}
                            />
                        }>
                            <a href="javascript:;" className="ant-dropdown-link">
                                <Icon type="down" />
                            </a>
                        </Dropdown>
                    }
                </span>
            ),
            title: 'Actions'
        }
    ];

    public attritionRequestColumns: Array<ITableCell> = [
        {
            dataIndex: 'terminationRequest.requestDate',
            key: 'requestDate',
            title: 'Request Date',
        },
        {
            dataIndex: 'terminationRequest.terminationDate',
            key: 'terminationDate',
            title: 'Termination Date',
        },
        {
            dataIndex: 'isReseller',
            key: 'clientType',
            render: (text: string, record: IAttritionByRequest) => (
                <span>
                    {record.isReseller ? 'Reseller' : 'End User'}
                </span>
            ),
            title: 'Client Type',
        },
        {
            dataIndex: 'id',
            key: 'companyId',
            title: 'Company ID',
        },
        {
            dataIndex: 'companyName',
            key: 'companyName',
            title: 'Company Name',
        },
        {
            dataIndex: 'terminationRequest.note.content',
            key: 'note',
            title: 'Note',
        },
        {
            dataIndex: 'terminationRequest.userProfileID',
            key: 'userProfileID',
            title: 'Author',
        },
        {
            dataIndex: 'terminationRequest.createTimestamp',
            key: 'createTimestamp',
            title: 'Submission Date',
        },
    ];

    public attritionDefaultColumns: Array<ITableCell> = [
        {
            dataIndex: 'serviceEndDate',
            key: 'serviceEndDate',
            title: 'Service End Date',
        },
        {
            dataIndex: 'isReseller',
            key: 'clientType',
            render: (text: string, record: IAttritionByRequest) => (
                <span>
                    {record.isReseller ? 'Reseller' : 'End User'}
                </span>
            ),
            title: 'Client Type',
        },
        {
            dataIndex: 'companyName',
            key: 'companyName',
            title: 'Company Name',
        },
    ];

    public constructor(props: ILocalProps) {
        super(props);

        // Set the initial local state
        this.state = {
            attritionsByDefault: null,
            attritionsByRequest: null,
            currentMonth: moment(),
            loading: true,
            requestedInMonth: null,
            terminateInMonth: null,

        }
    }

    public componentDidMount() {
        this.getReportData();
    }

    public addMonth = () => {
        this.setState({currentMonth: this.state.currentMonth.add(1, 'month')});
        this.getReportData();
    }

    public subMonth = () => {
        this.setState({currentMonth: this.state.currentMonth.add(-1, 'month')});
        this.getReportData();
    }

    public getReportData = async () => {
        this.setState({loading: true});
        const report = await getTerminationReport(this.state.currentMonth).catch(() => {
            this.props.logoutFunc();
            notification.error({ message: "Please log in" });
        });
        this.setState({
            attritionsByDefault: report ? report.attritionsByDefault : [],
            attritionsByRequest: report ? report.attritionsByRequest : [],
            loading: false,
            requestedInMonth: report ? report.requestedInMonth : [],
            terminateInMonth: report ?report.terminateInMonth : [],
        });
        return;
    }

    private handleExport = () => {
        if (this.state.attritionsByDefault && this.state.attritionsByDefault.length > 0) {
            this.triggerDownload(this.createCsv(this.attritionDefaultColumns, this.state.attritionsByDefault), 'Attritions By Default');
        }
        if (this.state.attritionsByRequest && this.state.attritionsByRequest.length > 0) {
            this.triggerDownload(this.createCsv(this.attritionRequestColumns, this.state.attritionsByRequest), 'Attritions By Request');
        }
        if (this.state.requestedInMonth && this.state.requestedInMonth.length > 0) {
            this.triggerDownload(this.createCsv(this.requestColumns, this.state.requestedInMonth), 'Requested In Month');
        }
        if (this.state.terminateInMonth && this.state.terminateInMonth.length > 0) {
            this.triggerDownload(this.createCsv(this.requestColumns, this.state.terminateInMonth), 'Terminate In Month');
        }
    }

    private createCsv(headers: ITableCell[], rows: any[]) {
        return headers.map((headerObject: ITableCell) => {
                // Get every column header except for actions
                if (headerObject.title === "Actions") {
                    return '';
                }
                return headerObject.title
            }) +
            '\n' +
            rows.map((rowObject: Object) => {
                // Get the relevant row values in the order of the header columns
                return headers.map((headerObject: ITableCell) => {
                    // Skip Actions
                    if (headerObject.title === "Actions") {
                        return '';
                    }
                    // Skip any columns without a data index
                    if (!headerObject.dataIndex) {
                        return '';
                    }

                    // Parse the index into its parts
                    let indexes = headerObject.dataIndex.split('.');

                    // Start at the row level
                    let data = rowObject;
                    indexes.forEach((index) => {
                        // Drill down to the final object defined by the index in the row object
                        if (data === null || !data.hasOwnProperty(index)) {
                            return;
                        }
                        data = data[index as keyof typeof data];
                    });

                    if (data === null) {
                        return '';
                    }
                    let rowString = data.toString();

                    if (rowString.search(/("|,|\n)/g) >= 0) {
                        rowString = `"${rowString}"`;
                    }

                    return rowString;
                })
            }).join('\n')
            + EOL;
    }

    private triggerDownload(csv: string, title: string) {
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        if (navigator.msSaveBlob) { // IE 10+
            navigator.msSaveBlob(blob, title);
        } else {
            const link = document.createElement('a');
            if (link.download !== undefined) {
                // Browsers that support HTML5 download attribute
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', title);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        }
    }

    public render() {
        return (
            <div>
                <h2 className="pull-left">Terminations</h2>
                <Button className="pull-right" onClick={this.handleExport}>Export</Button>
                <Button.Group className="pull-right">
                    <Button onClick={this.subMonth} className="btn-change-month">
                        <Icon type="left" />
                    </Button>
                    <Button className="btn-month">
                        {this.state.currentMonth.format('MMMM, YYYY')}
                    </Button>
                    <Button onClick={this.addMonth} className="btn-change-month">
                        <Icon type="right" />
                    </Button>
                </Button.Group>
                <Tabs type="card" className="new-line">
                    <TabPane tab="Requested In Month" key="1">
                        <Table
                            columns={this.requestColumns}
                            dataSource={this.state.requestedInMonth ? this.state.requestedInMonth : undefined}
                            loading={this.state.loading}
                        />
                    </TabPane>
                    <TabPane tab="Scheduled In Month" key="2">
                        <Table
                            columns={this.requestColumns}
                            dataSource={this.state.terminateInMonth ? this.state.terminateInMonth : undefined}
                            loading={this.state.loading}
                        />
                    </TabPane>
                    <TabPane tab="Attritions" key="3">
                        <h2>Attritions By Request</h2>
                        <Table
                            columns={this.attritionRequestColumns}
                            dataSource={this.state.attritionsByRequest ? this.state.attritionsByRequest : undefined}
                            loading={this.state.loading}
                        />
                        <h2>Attritions By Default</h2>
                        <Table
                            columns={this.attritionDefaultColumns}
                            dataSource={this.state.attritionsByDefault ? this.state.attritionsByDefault : undefined}
                            loading={this.state.loading}
                        />
                    </TabPane>
                </Tabs>
            </div>
        );
    }
}

export default Terminations;
